import React, { useMemo, useState } from "react";
import { useMutation, useQuery } from "@apollo/client";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import RefreshIcon from "@mui/icons-material/Refresh";
import SearchIcon from "@mui/icons-material/Search";
import { useSnackbar } from "notistack";
import { useAuth } from "../../hooks/useAuth";
import {
  BootstrapExtensionCandidate,
  BootstrapExtensionCandidatesData,
  GET_BOOTSTRAP_EXTENSION_CANDIDATES,
  TRIGGER_BOOTSTRAP_EXTENSION,
  TriggerBootstrapExtensionData,
} from "./queries";

const BootstrapExtension: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const { features } = useAuth();
  const canTrigger = features.some(
    (feature) =>
      feature.feature_name === "BOOTSTRAP-EXTENSION" && feature.update_access,
  );
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [confirming, setConfirming] =
    useState<BootstrapExtensionCandidate | null>(null);

  const {
    data,
    loading,
    error,
    refetch,
  } = useQuery<BootstrapExtensionCandidatesData>(
    GET_BOOTSTRAP_EXTENSION_CANDIDATES,
    { fetchPolicy: "network-only" },
  );
  const [trigger, { loading: triggering }] =
    useMutation<TriggerBootstrapExtensionData>(TRIGGER_BOOTSTRAP_EXTENSION);

  const candidates = data?.bootstrapExtensionCandidates.candidates;
  const filteredCandidates = useMemo(() => {
    const rows = candidates ?? [];
    const term = search.trim().toLowerCase();
    if (!term) return rows;
    return rows.filter(
      (candidate) =>
        candidate.iccid.toLowerCase().includes(term) ||
        candidate.vehicleId.toLowerCase().includes(term),
    );
  }, [candidates, search]);
  const visibleCandidates = filteredCandidates.slice(
    page * rowsPerPage,
    (page + 1) * rowsPerPage,
  );

  const submitTrigger = async () => {
    if (!confirming) return;
    try {
      const { data: resultData } = await trigger({
        variables: { iccid: confirming.iccid },
      });
      const result = resultData?.triggerBootstrapExtension;
      if (!result) {
        enqueueSnackbar("The server returned no result for the request.", {
          variant: "error",
        });
        return;
      }
      enqueueSnackbar(result.message, {
        variant: result.success ? "success" : "error",
      });
      setConfirming(null);
      setPage(0);
      try {
        await refetch();
      } catch (refreshError) {
        enqueueSnackbar(
          `Request result received, but the candidate list could not refresh: ${
            refreshError instanceof Error
              ? refreshError.message
              : "unknown error"
          }`,
          { variant: "warning" },
        );
      }
    } catch (mutationError) {
      enqueueSnackbar(
        mutationError instanceof Error
          ? mutationError.message
          : "Failed to trigger bootstrap extension.",
        { variant: "error" },
      );
    }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Box
        sx={{
          display: "flex",
          alignItems: { xs: "stretch", sm: "center" },
          justifyContent: "space-between",
          flexDirection: { xs: "column", sm: "row" },
          gap: 2,
          mb: 2,
        }}
      >
        <Box>
          <Typography variant="h4" fontWeight={700}>
            Bootstrap Extension
          </Typography>
          <Typography color="text.secondary">
            Suspended SIMs currently eligible for a Bootstrap reactivation request.
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={() => void refetch()}
          disabled={loading}
        >
          Refresh
        </Button>
      </Box>

      <Alert severity="info" sx={{ mb: 2 }}>
        Each trigger sends a separate request to Sensorise. Eligibility and SIM
        status are checked again when you confirm. Only one ICCID can be
        triggered at a time.
      </Alert>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load candidates: {error.message}
        </Alert>
      )}

      <Paper variant="outlined">
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 2,
            p: 2,
            flexWrap: "wrap",
          }}
        >
          <Typography fontWeight={600}>
            Eligible ICCIDs ({filteredCandidates.length}
            {search ? ` of ${candidates?.length ?? 0}` : ""})
          </Typography>
          <TextField
            size="small"
            value={search}
            onChange={(event) => {
              setSearch(event.target.value);
              setPage(0);
            }}
            placeholder="Search ICCID or vehicle ID"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
        </Box>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>ICCID</TableCell>
                <TableCell>Vehicle ID</TableCell>
                <TableCell>Readiness</TableCell>
                <TableCell align="right">Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && !data ? (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ py: 5 }}>
                    <CircularProgress size={28} />
                  </TableCell>
                </TableRow>
              ) : visibleCandidates.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                    {search
                      ? "No ICCIDs match this search."
                      : "No eligible ICCIDs found."}
                  </TableCell>
                </TableRow>
              ) : (
                visibleCandidates.map((candidate) => (
                  <TableRow key={`${candidate.vehicleId}-${candidate.iccid}`}>
                    <TableCell sx={{ fontFamily: "monospace" }}>
                      {candidate.iccid}
                    </TableCell>
                    <TableCell>{candidate.vehicleId}</TableCell>
                    <TableCell>
                      {candidate.ready
                        ? "Ready"
                        : `Missing ${candidate.missing.join(", ")}`}
                    </TableCell>
                    <TableCell align="right">
                      <Button
                        size="small"
                        variant="contained"
                        color="primary"
                        startIcon={<PlayArrowIcon />}
                        disabled={!canTrigger || !candidate.ready || triggering}
                        onClick={() => setConfirming(candidate)}
                      >
                        Trigger
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={filteredCandidates.length}
          page={page}
          onPageChange={(_event, nextPage) => setPage(nextPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(event) => {
            setRowsPerPage(Number(event.target.value));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 20, 50, 100]}
        />
      </Paper>

      {!canTrigger && (
        <Alert severity="warning" sx={{ mt: 2 }}>
          You have read-only access. Ask an administrator for BOOTSTRAP-EXTENSION
          update permission to submit requests.
        </Alert>
      )}

      <Dialog
        open={Boolean(confirming)}
        onClose={() => {
          if (!triggering) setConfirming(null);
        }}
      >
        <DialogTitle>Trigger Bootstrap Extension?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will submit a Bootstrap reactivation request to Sensorise for
            ICCID <strong>{confirming?.iccid}</strong>. The server will verify
            the ICCID is still eligible before sending.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setConfirming(null)}
            disabled={triggering}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => void submitTrigger()}
            disabled={triggering}
            startIcon={
              triggering ? <CircularProgress size={16} color="inherit" /> : <PlayArrowIcon />
            }
          >
            {triggering ? "Submitting..." : "Confirm trigger"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BootstrapExtension;
